import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
	Autocomplete,
	Box,
	IconButton,
	MenuItem,
	TextField,
	Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";

import type {
	CatalogOption,
	ProductSizeOption,
	StandOption,
} from "../models/RFactorPerStand";

interface Props {
	start: Moment;
	end: Moment;
	productTypeId: number | null;
	productSizeId: number | null;
	crewId: string | null;
	standId: number | null;
	productTypes: CatalogOption[];
	productSizes: ProductSizeOption[];
	crews: string[];
	stands: StandOption[];
	onStartChange: (date: Moment | null) => void;
	onEndChange: (date: Moment | null) => void;
	onProductTypeChange: (id: number | null) => void;
	onProductSizeChange: (id: number | null) => void;
	onCrewChange: (crew: string | null) => void;
	onStandChange: (id: number | null) => void;
	onReset: () => void;
	isLoading: boolean;
}

// Same order the legacy lays them out, left to right. The Stand control is the
// one addition: the legacy renders it without a label, because the resource
// string for it was never written.
export const RFactorPerStandToolbar = ({
	start,
	end,
	productTypeId,
	productSizeId,
	crewId,
	standId,
	productTypes,
	productSizes,
	crews,
	stands,
	onStartChange,
	onEndChange,
	onProductTypeChange,
	onProductSizeChange,
	onCrewChange,
	onStandChange,
	onReset,
	isLoading,
}: Props) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
			px: 1.5,
			py: 0.75,
			flexWrap: "nowrap",
		}}
	>
		<Box sx={{ flex: "0 0 150px" }}>
			<DatePicker
				label="Start"
				format="MM/DD/YYYY"
				value={start}
				maxDate={end}
				onChange={onStartChange}
				slotProps={{ textField: { size: "small", fullWidth: true } }}
			/>
		</Box>
		<Box sx={{ flex: "0 0 150px" }}>
			<DatePicker
				label="End"
				format="MM/DD/YYYY"
				value={end}
				minDate={start}
				onChange={onEndChange}
				slotProps={{ textField: { size: "small", fullWidth: true } }}
			/>
		</Box>

		<TextField
			select
			size="small"
			label="Product Type"
			slotProps={{
				inputLabel: { shrink: true },
				select: { displayEmpty: true },
			}}
			sx={{ flex: "0 0 170px" }}
			value={productTypeId ?? ""}
			onChange={(e) =>
				onProductTypeChange(
					e.target.value === "" ? null : Number(e.target.value),
				)
			}
		>
			<MenuItem value="">All</MenuItem>
			{productTypes.map((type) => (
				<MenuItem key={type.Id} value={type.Id}>
					{type.Name}
				</MenuItem>
			))}
		</TextField>

		{/* The mill runs well over a hundred sizes and a range can offer fifty of
		    them, so this one is typed rather than scrolled. Same control the
		    Product Changes report uses. */}
		<Autocomplete
			size="small"
			sx={{ flex: "0 0 230px" }}
			options={productSizes}
			value={productSizes.find((s) => s.Id === productSizeId) ?? null}
			getOptionLabel={(option) => option.Name}
			isOptionEqualToValue={(option, selected) => option.Id === selected.Id}
			onChange={(_event, option) => onProductSizeChange(option?.Id ?? null)}
			autoHighlight
			renderOption={(props, option) => {
				const { key, ...rest } = props as typeof props & { key: string };
				return (
					<li key={option.Id} {...rest}>
						{option.Name}
					</li>
				);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Product"
					// MUI hides the placeholder while the label still sits inside the
					// field, which is exactly when the hint is needed.
					slotProps={{ inputLabel: { shrink: true } }}
					placeholder="Search product..."
				/>
			)}
		/>

		<TextField
			select
			size="small"
			label="Shift"
			slotProps={{
				inputLabel: { shrink: true },
				select: { displayEmpty: true },
			}}
			sx={{ flex: "0 0 100px" }}
			value={crewId ?? ""}
			onChange={(e) =>
				onCrewChange(e.target.value === "" ? null : String(e.target.value))
			}
		>
			<MenuItem value="">All</MenuItem>
			{crews.map((crew) => (
				<MenuItem key={crew} value={crew}>
					{crew}
				</MenuItem>
			))}
		</TextField>

		<TextField
			select
			size="small"
			label="Stand"
			sx={{ flex: "0 0 110px" }}
			value={standId ?? ""}
			onChange={(e) =>
				onStandChange(e.target.value === "" ? null : Number(e.target.value))
			}
		>
			{stands.map((stand) => (
				<MenuItem key={stand.Id} value={stand.Id}>
					{stand.Name}
				</MenuItem>
			))}
		</TextField>

		<Tooltip title="Reset to the default range">
			<span>
				<IconButton
					onClick={onReset}
					disabled={isLoading}
					size="small"
					sx={{
						bgcolor: "primary.main",
						color: "white",
						width: 28,
						height: 28,
						"&:hover": { bgcolor: "primary.dark" },
						"&.Mui-disabled": {
							bgcolor: "action.disabledBackground",
							color: "action.disabled",
						},
					}}
				>
					<RestartAltIcon sx={{ fontSize: 18 }} />
				</IconButton>
			</span>
		</Tooltip>
	</Box>
);
